/**
 * THE ROUTER. Nothing else.
 *
 * This file stays a table of addresses on purpose. The architecture is explicit that Legacy's
 * single-file frontend — an `index.html` carrying 85,000 lines of uncommitted diff — is the thing
 * not to reproduce. Every screen lives in its own file and is reached by an address from
 * `routes.ts`, which is the only place a URL is constructed (09 §1.5).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ContextProvider } from './shell/ContextProvider';
import { callOperation, sessionFromWhoami } from './api/operations';
import type { CompanySummary, SessionSummary } from './api/contract';
import { listOf, useServerData } from './api/useServerData';
import { SignInScreen } from './screens/SignInScreen';
import { CompanyPickerScreen } from './screens/CompanyPickerScreen';
import { CompanyHomeScreen } from './screens/CompanyHomeScreen';
import { ModuleHomeScreen } from './screens/ModuleHomeScreen';
import { ParentScreenPlaceholder } from './screens/ParentScreenPlaceholder';
import { PlatformConsoleScreen } from './screens/PlatformConsoleScreen';
import { PlatformTenantsScreen } from './screens/PlatformTenantsScreen';
import { PlatformTenantScreen } from './screens/PlatformTenantScreen';
import { PlatformTenantProvisioningScreen } from './screens/PlatformTenantProvisioningScreen';
import { PlatformSubscriptionScreen } from './screens/PlatformSubscriptionScreen';
import { PlatformCommercialScreen } from './screens/PlatformCommercialScreen';
import { PlatformSupportGrantsScreen } from './screens/PlatformSupportGrantsScreen';
import { TenantAdminScreen } from './screens/TenantAdminScreen';
import { TenantUsersScreen } from './screens/TenantUsersScreen';
import { TenantUserInvitationScreen } from './screens/TenantUserInvitationScreen';
import { TenantCompaniesScreen } from './screens/TenantCompaniesScreen';
import { TenantCompanyOnboardingScreen } from './screens/TenantCompanyOnboardingScreen';
import { TenantCompanyScreen } from './screens/TenantCompanyScreen';
import { TenantRolesScreen } from './screens/TenantRolesScreen';
import { TenantRoleAuthoringScreen } from './screens/TenantRoleAuthoringScreen';
import { TenantCompanyAccessScreen } from './screens/TenantCompanyAccessScreen';
import { TenantSubscriptionScreen } from './screens/TenantSubscriptionScreen';
import { SourceConnectionsScreen } from './screens/SourceConnectionsScreen';
import { SourceConnectionScreen } from './screens/SourceConnectionScreen';
import { TenantSecurityScreen } from './screens/TenantSecurityScreen';
import { PersonalSecurityScreen } from './screens/PersonalSecurityScreen';
import { MfaEnrolmentScreen } from './screens/MfaEnrolmentScreen';
import { MfaChallengeScreen } from './screens/MfaChallengeScreen';
import { CredentialSetupScreen } from './screens/CredentialSetupScreen';
import { BlockedDocumentScreen } from './screens/BlockedDocumentScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { onUnauthenticated } from './api/client';
import {
  fixtureModules,
  fixturePeriods,
  fixturesEnabled,
} from './api/fixtures';
import { routePatterns, routes } from './routes';

/** Route params are read here and handed to screens as plain props, so screens stay testable. */
function CompanyHomeRoute(): JSX.Element {
  const { companyUid } = useParams<{ companyUid: string }>();
  if (companyUid === undefined) return <NotFoundScreen />;
  return <CompanyHomeScreen companyUid={companyUid} />;
}

function ModuleHomeRoute(): JSX.Element {
  const { companyUid, moduleCode } = useParams<{ companyUid: string; moduleCode: string }>();
  if (companyUid === undefined || moduleCode === undefined) return <NotFoundScreen />;
  return <ModuleHomeScreen companyUid={companyUid} moduleCode={moduleCode} />;
}

function ParentScreenRoute(): JSX.Element {
  const { companyUid, moduleCode, parent } = useParams<{
    companyUid: string;
    moduleCode: string;
    parent: string;
  }>();
  if (companyUid === undefined || moduleCode === undefined || parent === undefined) {
    return <NotFoundScreen />;
  }
  return <ParentScreenPlaceholder companyUid={companyUid} moduleCode={moduleCode} parent={parent} />;
}

function PlatformTenantRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformTenantScreen tenantUid={tenantUid} />;
}

function PlatformSubscriptionRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformSubscriptionScreen tenantUid={tenantUid} />;
}

function PlatformCommercialRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformCommercialScreen tenantUid={tenantUid} />;
}

function TenantCompanyRoute(): JSX.Element {
  const { companyUid } = useParams<{ companyUid: string }>();
  if (companyUid === undefined) return <NotFoundScreen />;
  return <TenantCompanyScreen companyUid={companyUid} />;
}

function SourceConnectionRoute(): JSX.Element {
  const { connectionUid } = useParams<{ connectionUid: string }>();
  if (connectionUid === undefined) return <NotFoundScreen />;
  return <SourceConnectionScreen connectionUid={connectionUid} />;
}

/**
 * The second factor's challenge reference travels in the ADDRESS, because there is no session yet.
 *
 * Read here rather than in the screen, so the screen stays renderable without a router — the same
 * reason `CompanyHomeRoute` and the others exist.
 */
function MfaChallengeRoute(): JSX.Element {
  const [params] = useSearchParams();
  return <MfaChallengeScreen challengeRef={params.get('challenge')} />;
}

/**
 * The two mailed-token addresses. `kind` is stated by the ROUTE, never sniffed from the token:
 * the two acts have different consequences and a token that resolved to the wrong one would be
 * creating an account where somebody meant to reset a password.
 */
function InvitationAcceptRoute(): JSX.Element {
  const [params] = useSearchParams();
  return <CredentialSetupScreen kind="invitation" token={params.get('token')} />;
}

function PasswordResetRoute(): JSX.Element {
  const [params] = useSearchParams();
  return <CredentialSetupScreen kind="password_reset" token={params.get('token')} />;
}

function BlockedDocumentRoute(): JSX.Element {
  const { companyUid, documentUid } = useParams<{ companyUid: string; documentUid: string }>();
  if (companyUid === undefined || documentUid === undefined) return <NotFoundScreen />;
  return <BlockedDocumentScreen companyUid={companyUid} documentUid={documentUid} />;
}

/**
 * The read a surface with no company list performs: none.
 *
 * Module level so it is REFERENTIALLY STABLE. `useServerData` re-runs whenever its thunk changes, so
 * an inline `() => Promise.resolve(...)` here would re-fire on every render — forever, on the
 * platform surface, which is the one surface that should be asking for nothing.
 */
const NO_COMPANIES = (): Promise<{ readonly companies: readonly CompanySummary[] }> =>
  Promise.resolve({ companies: [] });

export function App(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  // 401 ⇒ sign-in. The client raises it; the router acts on it. Neither decides *why*.
  useEffect(() => {
    onUnauthenticated(() => navigate(routes.signIn));
  }, [navigate]);

  const go = useCallback((path: string) => navigate(path), [navigate]);

  /*
   * THE SESSION IS READ FROM THE SERVER. **CM-030, and the fix for CM-014.**
   *
   * `auth.whoami.read` was declared, classified reachable, and called by nothing — so this line used
   * to mint a session from `location.pathname`, and typing `/platform` granted the platform surface.
   * The route is now called on mount and on every address change, which is also what makes a cold
   * load work: the surface is re-read rather than remembered, so there is nothing for a plain
   * `<a href>` navigation to discard.
   *
   * A 401 is NOT handled here. `onUnauthenticated` above already routes to sign-in, and a second
   * decision about what an unauthenticated read means is a second place for the two to disagree.
   */
  const [session, setSession] = useState<SessionSummary | null>(null);

  useEffect(() => {
    let current = true;
    void callOperation('auth.whoami.read', {}).then(
      (me) => {
        if (current) setSession(sessionFromWhoami(me));
      },
      () => {
        // Left null. There is no fallback surface: a session the server did not state is not a
        // session, and guessing one is the defect CM-014 records.
        if (current) setSession(null);
      },
    );
    return () => {
      current = false;
    };
  }, [location.pathname]);

  /*
   * THE COMPANY LIST IS READ FROM THE SERVER TOO. **Phase 2 §6, vertical A.**
   *
   * It is read HERE rather than in `CompanyPickerScreen` because it is not that screen's data: the
   * context bar names the current company on every screen in the product, `TenantCompaniesScreen`
   * and `TenantCompanyAccessScreen` both render it, and 04 I6 requires ONE source for the working
   * context. Four screens each fetching their own list is four answers to one question.
   *
   * ── IT IS SCOPED TO A TENANT SESSION, AND THAT IS NOT AN OPTIMISATION ────────────────────────
   *
   * A platform operator has no companies — GL-POINT's own staff hold no client access, which is the
   * separation the whole SupportAccess model exists to preserve. Asking for the list on a platform
   * surface would be asking the gate a question whose only honest answer is a refusal, and a refusal
   * on every platform page load is noise that trains people to ignore refusals.
   *
   * ── AND AN EMPTY LIST IS NOT A FAILED ONE ────────────────────────────────────────────────────
   *
   * `visibleCompanies` is what a screen renders "you have access to no companies" from. If a failed
   * read collapsed to `[]`, an office whose read was refused would be told they have no clients —
   * so the failure is carried, and `<ServerRead>` on the screens below is what shows it.
   */
  const surface = session?.sessionContext ?? null;
  const readCompanies = useCallback(() => callOperation('companies.list.read', {}), []);
  const companiesRead = useServerData(surface === 'TENANT' ? readCompanies : NO_COMPANIES);

  const companies = listOf(companiesRead.result, 'companies');
  const modules = fixturesEnabled() ? fixtureModules : [];
  const periods = fixturesEnabled() ? fixturePeriods : [];

  return (
    <ContextProvider
      session={session}
      companies={companies}
      companiesResult={companiesRead.result}
      modules={modules}
      periods={periods}
      navigate={go}
    >
      <Routes>
        <Route path="/" element={<Navigate to={routes.companies} replace />} />
        <Route
          path={routePatterns.signIn}
          element={
            <SignInScreen
              onSignedIn={() => navigate(routes.companies)}
              // A CHALLENGE IS NOT A SESSION. Both of these carry the ref forward and go nowhere
              // near the company list — the principal holds no cookie yet, so routing them into the
              // product would show an empty shell and a denial, and read as a broken sign-in rather
              // than as the second step it is.
              onMfaRequired={(challengeRef) =>
                navigate(`${routes.mfaChallenge}?challenge=${encodeURIComponent(challengeRef)}`)
              }
              onMfaEnrolmentRequired={(challengeRef) =>
                navigate(`${routes.mfaEnrolment}?challenge=${encodeURIComponent(challengeRef)}`)
              }
            />
          }
        />
        <Route path={routePatterns.companies} element={<CompanyPickerScreen />} />
        <Route path={routePatterns.companyHome} element={<CompanyHomeRoute />} />
        <Route path={routePatterns.moduleHome} element={<ModuleHomeRoute />} />
        <Route path={routePatterns.parentScreen} element={<ParentScreenRoute />} />
        <Route path={routePatterns.blockedDocument} element={<BlockedDocumentRoute />} />
        <Route path={routePatterns.platformConsole} element={<PlatformConsoleScreen />} />

        {/* Control Plane. A static segment out-ranks a parameter, so `/new` never reaches the
            detail screen — and `uid()` would refuse to build such an address anyway. */}
        <Route path={routePatterns.platformTenants} element={<PlatformTenantsScreen />} />
        <Route
          path={routePatterns.platformTenantProvisioning}
          element={<PlatformTenantProvisioningScreen />}
        />
        <Route path={routePatterns.platformSupportGrants} element={<PlatformSupportGrantsScreen />} />
        <Route path={routePatterns.platformTenant} element={<PlatformTenantRoute />} />
        <Route
          path={routePatterns.platformTenantSubscription}
          element={<PlatformSubscriptionRoute />}
        />
        {/* Money, deliberately apart from capacity: 08 §8.4 / CF-66. */}
        <Route path={routePatterns.platformTenantCommercial} element={<PlatformCommercialRoute />} />

        {/* Tenant administration. No tenant uid rides in these addresses: the active tenant is a
            property of the session, and a URL that named one would be a second source for it. */}
        <Route path={routePatterns.tenantAdmin} element={<TenantAdminScreen />} />
        <Route path={routePatterns.tenantUsers} element={<TenantUsersScreen />} />
        <Route path={routePatterns.tenantUserInvitation} element={<TenantUserInvitationScreen />} />
        <Route path={routePatterns.tenantCompanies} element={<TenantCompaniesScreen />} />
        {/* Static before parameter, as above: `/new` and `/authoring` are not uids and never
            reach a detail screen — `uid()` would refuse to build such an address anyway. */}
        <Route
          path={routePatterns.tenantCompanyOnboarding}
          element={<TenantCompanyOnboardingScreen />}
        />
        <Route path={routePatterns.tenantCompany} element={<TenantCompanyRoute />} />
        <Route path={routePatterns.tenantRoles} element={<TenantRolesScreen />} />
        <Route path={routePatterns.tenantRoleAuthoring} element={<TenantRoleAuthoringScreen />} />
        <Route path={routePatterns.tenantCompanyAccess} element={<TenantCompanyAccessScreen />} />
        <Route path={routePatterns.tenantSubscription} element={<TenantSubscriptionScreen />} />
        <Route path={routePatterns.tenantSources} element={<SourceConnectionsScreen />} />
        <Route path={routePatterns.tenantSourceConnection} element={<SourceConnectionRoute />} />
        <Route path={routePatterns.tenantSecurity} element={<TenantSecurityScreen />} />

        {/* Identity belongs to the ACCOUNT, so these are reachable from either surface. */}
        <Route path={routePatterns.personalSecurity} element={<PersonalSecurityScreen />} />
        <Route path={routePatterns.mfaEnrolment} element={<MfaEnrolmentScreen />} />
        <Route path={routePatterns.mfaChallenge} element={<MfaChallengeRoute />} />
        <Route path={routePatterns.invitationAccept} element={<InvitationAcceptRoute />} />
        <Route path={routePatterns.passwordReset} element={<PasswordResetRoute />} />

        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </ContextProvider>
  );
}
